export type ReportModule = 'performspark';

export interface ReportModuleConfig {
    key: ReportModule;
    label: string;
    description: string;
}

export const REPORT_MODULES: ReportModuleConfig[] = [
    {
        key: 'performspark',
        label: 'Perform Spark',
        description: 'Performance management and spark analytics reports'
    }
];

// ReportType is dynamic and fetched from API per module
// Stored as string (numeric ID) to match API request format
export type ReportType = string;

// Data types from API schema
export type ColumnDataType = 'string' | 'integer' | 'decimal' | 'date' | 'timestamp' | 'boolean';

// Filter operators by data type
export type FilterOperator = '=' | '!=' | '>' | '<' | '>=' | '<=' | 'in' | 'like';

export const FILTER_OPERATORS_BY_TYPE: Record<ColumnDataType, { value: FilterOperator; label: string }[]> = {
    string: [
        { value: '=', label: 'Equals' },
        { value: '!=', label: 'Not Equals' },
        { value: 'in', label: 'In List' },
        { value: 'like', label: 'Contains' }
    ],
    integer: [
        { value: '=', label: 'Equals' },
        { value: '!=', label: 'Not Equals' },
        { value: '>', label: 'Greater Than' },
        { value: '<', label: 'Less Than' },
        { value: '>=', label: 'Greater or Equal' },
        { value: '<=', label: 'Less or Equal' },
        { value: 'in', label: 'In List' }
    ],
    decimal: [
        { value: '=', label: 'Equals' },
        { value: '!=', label: 'Not Equals' },
        { value: '>', label: 'Greater Than' },
        { value: '<', label: 'Less Than' },
        { value: '>=', label: 'Greater or Equal' },
        { value: '<=', label: 'Less or Equal' },
        { value: 'in', label: 'In List' }
    ],
    date: [
        { value: '=', label: 'Equals' },
        { value: '!=', label: 'Not Equals' },
        { value: '>', label: 'After' },
        { value: '<', label: 'Before' },
        { value: '>=', label: 'On or After' },
        { value: '<=', label: 'On or Before' }
    ],
    timestamp: [
        { value: '=', label: 'Equals' },
        { value: '!=', label: 'Not Equals' },
        { value: '>', label: 'After' },
        { value: '<', label: 'Before' },
        { value: '>=', label: 'On or After' },
        { value: '<=', label: 'On or Before' }
    ],
    boolean: [
        { value: '=', label: 'Equals' },
        { value: '!=', label: 'Not Equals' }
    ]
};

export interface Column {
    id: string;
    label: string;
    visible: boolean;
    sort?: 'asc' | 'desc' | null;
    filter?: string;
    filterOperator?: FilterOperator;
    type: 'text' | 'number' | 'date' | 'currency';
    dataType: ColumnDataType; // Raw data type from API for filter operator validation
    filterable?: boolean;
    sortable?: boolean;
    isMandatory?: boolean; // If true, visibility cannot be toggled off
}

export interface DateRange {
    type: '7d' | '30d' | 'all_time' | 'custom' | 'from_date';
    startDate: string;
    endDate: string;
}

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export type MonthlyOrdinal = 'first' | 'last';
export type MonthlyDayType = 'day' | 'weekday' | DayOfWeek;

// ============================================
// UI Types
// ============================================

export interface Recipient {
    userId?: string;      // Internal user ID
    email: string;        // Email (used for both internal display and external)
    name?: string;        // Display name for internal users
    isExternal: boolean;
}

export interface Schedule {
    frequency: 'once' | 'daily' | 'weekly' | 'monthly';
    time: string;
    recipients: Recipient[];
    dayOfWeek?: DayOfWeek; // For weekly frequency
    monthlyOrdinal?: MonthlyOrdinal; // For monthly frequency
    monthlyDayType?: MonthlyDayType; // For monthly frequency
}

export type ReportStatus = 'active' | 'paused' | 'draft';

export interface ReportConfig {
    id?: string;
    module: ReportModule;
    name: string;
    description: string;
    type: ReportType;
    dateRange: DateRange;
    columns: Column[];
    schedule: Schedule;
    status?: ReportStatus;
}

export interface ReportTypeOption {
    value: string;        // Numeric ID as string - sent to API as report_type
    metricKey: string;    // Metric key string - used for fetching schema
    label: string;
    description: string;
    defaultColumns: Column[];
}

// ============================================
// API Types
// ============================================

export interface FilterSpec {
    field: string;
    operator: FilterOperator;
    value: string;
}

// API filter format - JSONB object with field names as keys
export interface ApiFilterSpec {
    operator: string;
    value: string | string[];
}

export type ApiFilters = Record<string, ApiFilterSpec>;

export interface OrderSpec {
    field: string;
    direction: 'asc' | 'desc';
}

// API Duration - supports both relative and absolute formats
export interface ApiDurationRelative {
    type: 'relative';
    value: string;
    unit: 'days' | 'weeks' | 'months' | 'years';
}

export interface ApiDurationAbsolute {
    type: 'absolute';
    start_date: string;
    end_date: string;
}

export type ApiDuration = ApiDurationRelative | ApiDurationAbsolute;

// TimeRange - new format from backend (replaces ApiDuration in responses)
export interface TimeRange {
    start?: string;      // ISO 8601: "2024-01-01T00:00:00Z"
    end?: string;        // ISO 8601: "2024-12-31T23:59:59Z"
    relative?: string;   // "7d", "30d", "90d", "365d", "1y", "ytd", "mtd", "qtd", "all"
}

export interface ApiRecipient {
    user_id?: string;  // user's user_uuid (bigint as string) for internal users
    email?: string;
    external: boolean;
}

export interface ApiSchedule {
    frequency: 'once' | 'daily' | 'weekly' | 'monthly';
    time: string;
    dayOfWeek?: DayOfWeek;
    monthlyOrdinal?: MonthlyOrdinal;
    monthlyDayType?: MonthlyDayType;
}

export interface CustomReportApiRequest {
    id?: string;
    name: string;
    description?: string;
    module?: string;
    report_type: string;
    columns: string[];
    filters: ApiFilters;
    sort: OrderSpec[];
    duration: ApiDuration;
    schedule: ApiSchedule | Record<string, never>;  // Empty object for non-scheduled reports
    recepients: ApiRecipient[];  // Note: API uses "recepients" (typo in backend)
}

// Update request uses add/remove for recipients
export interface UpdateReportApiRequest {
    name?: string;
    description?: string;
    columns?: string[];
    filters?: ApiFilters;
    sort?: OrderSpec[];
    duration?: ApiDuration;
    schedule?: ApiSchedule | Record<string, never>;
    add_recepients?: ApiRecipient[];
    remove_recepients?: ApiRecipient[];
}

export interface CustomReportApiResponse {
    id: string;
    name: string;
    description?: string;
    module?: string;
    report_type: number;
    report_type_name: string;
    metric_key: string;
    columns: string[] | null;
    filters: FilterSpec[] | null;        // Changed: Array format instead of object
    order_by: OrderSpec[] | null;        // Changed: Renamed from 'sort'
    time_range: TimeRange | null;        // Changed: Renamed from 'duration' with new structure
    schedule: ApiSchedule | null;
    recepients?: ApiRecipient[];
    status: ReportStatus;
    created_at: string;
    updated_at: string;
    next_run_at: string | null;
    last_run_at: string | null;
    created_by: string;
    created_by_name?: string;
    account_id: string;
    consecutive_failures: number;
}

export interface ReportRunHistory {
    id: string;
    custom_report_id: string;
    status: 'Completed' | 'Failed' | 'Pending' | 'Running';
    comments?: string;
    row_count?: number;   // Optional - removed from backend response
    success?: boolean;    // Optional - removed from backend response
    created_at: string;
    updated_at: string;
}

// ============================================
// Generated Reports (for recipients)
// ============================================

export interface GeneratedReport {
    id: string;
    custom_report_id: string;
    custom_report_name: string;
    report_type_name: string;
    row_count?: number;   // Optional - removed from backend list response
    success?: boolean;    // Optional - removed from backend list response
    created_at: string;
    updated_at: string;
}

export interface ReportConfiguration {
    columns?: string[];
    filters?: ApiFilters;
    sort?: OrderSpec[];
    duration?: ApiDuration;
}

export interface GeneratedReportDetail extends GeneratedReport {
    status: 'Completed' | 'Failed' | 'Pending' | 'Running';
    comments?: string;
    data: {
        data: Record<string, unknown>[];
        columns: string[];
        success: boolean;
        metadata: {
            row_count: number;
            query_time: string;
        };
    };
    metric_key: string;
    configuration: ReportConfiguration;
}

export interface GeneratedReportsResponse {
    success: boolean;
    message: string;
    data: GeneratedReport[];
    total: number;
    limit: number;
    offset: number;
}
